import React, { useCallback, useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import {
  approvePayment,
  calculateDividendPayroll,
  deletePayment,
  getClients,
  getPayments,
  markPaymentPaid,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { StatusBadge } from '../components/app';
import { useAuth } from '../contexts/AuthContext';
import {
  Calculator,
  Check,
  Clock,
  CurrencyDollar,
  HandCoins,
  Receipt,
  TrendDown,
  TrendUp,
  Trash,
  UsersThree,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const currentMonth = () => new Date().toISOString().slice(0, 7);
const currentYear = new Date().getFullYear();

const monthOptions = [
  { value: '01', es: 'Enero', en: 'January' },
  { value: '02', es: 'Febrero', en: 'February' },
  { value: '03', es: 'Marzo', en: 'March' },
  { value: '04', es: 'Abril', en: 'April' },
  { value: '05', es: 'Mayo', en: 'May' },
  { value: '06', es: 'Junio', en: 'June' },
  { value: '07', es: 'Julio', en: 'July' },
  { value: '08', es: 'Agosto', en: 'August' },
  { value: '09', es: 'Septiembre', en: 'September' },
  { value: '10', es: 'Octubre', en: 'October' },
  { value: '11', es: 'Noviembre', en: 'November' },
  { value: '12', es: 'Diciembre', en: 'December' },
];

const yearOptions = Array.from({ length: 7 }, (_, index) => String(currentYear - 3 + index));

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const createClientForm = () => ({
  monthly_income: '',
  monthly_expenses: '',
  reserve_percentage: '0',
  shareholder_count: '3',
  notes: '',
});

export function PaymentsPage() {
  const { t, isSpanish } = useLanguage();
  const { isHR, isManager } = useAuth();
  const [payments, setPayments] = useState([]);
  const [clients, setClients] = useState([]);
  const [formsByClient, setFormsByClient] = useState({});
  const [selectedMonth, setSelectedMonth] = useState(currentMonth());
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [submittingClientId, setSubmittingClientId] = useState(null);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  const [selectedYear, selectedMonthNumber] = selectedMonth.split('-');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { payment_kind: 'dividend_distribution' };
      if (statusFilter !== 'all') params.status = statusFilter;

      const [paymentsRes, clientsRes] = await Promise.all([
        getPayments(params),
        getClients({ is_active: true }),
      ]);

      setPayments(paymentsRes.data);
      setClients(clientsRes.data);
      setFormsByClient((prev) => {
        const next = { ...prev };
        clientsRes.data.forEach((client) => {
          if (!next[client._id]) next[client._id] = createClientForm();
        });
        return next;
      });
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setClientFormValue = (clientId, field, value) => {
    setFormsByClient((prev) => ({
      ...prev,
      [clientId]: {
        ...(prev[clientId] || createClientForm()),
        [field]: value,
      },
    }));
  };

  const calculatePreview = (form) => {
    const income = Number(form?.monthly_income || 0);
    const expenses = Number(form?.monthly_expenses || 0);
    const reservePct = Number(form?.reserve_percentage || 0);
    const net = income - expenses;
    const reserve = net > 0 ? net * (reservePct / 100) : 0;
    const distributable = Math.max(net - reserve, 0);
    const count = Math.max(Number.parseInt(form?.shareholder_count || 0, 10), 0);
    return {
      income,
      expenses,
      net,
      reserve,
      distributable,
      perShareholder: count ? distributable / count : 0,
      count,
    };
  };

  const handleCalculateDividends = async (client) => {
    const form = formsByClient[client._id] || createClientForm();
    if (!selectedMonth) {
      toast.error(isSpanish ? 'Selecciona el mes' : 'Select the month');
      return;
    }
    const shareholderCount = Number.parseInt(form.shareholder_count || 0, 10);
    if (!shareholderCount || shareholderCount < 1) {
      toast.error(isSpanish ? 'Ingresa al menos un accionista' : 'Enter at least one shareholder');
      return;
    }

    setSubmittingClientId(client._id);
    try {
      const payload = {
        client_id: client._id,
        period_month: selectedMonth,
        monthly_income: Number(form.monthly_income || 0),
        monthly_expenses: Number(form.monthly_expenses || 0),
        reserve_percentage: Number(form.reserve_percentage || 0),
        shareholder_count: shareholderCount,
        notes: form.notes || '',
      };
      const response = await calculateDividendPayroll(payload);
      toast.success(response.data.calculation_action === 'updated'
        ? (isSpanish ? 'Linea de dividendos actualizada' : 'Dividend line updated')
        : (isSpanish ? 'Linea de dividendos generada' : 'Dividend line generated')
      );
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSubmittingClientId(null);
    }
  };

  const handleApprove = async (id) => {
    try {
      await approvePayment(id);
      toast.success(t('successApproved'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleMarkPaid = async (id) => {
    try {
      await markPaymentPaid(id);
      toast.success(isSpanish ? 'Pago marcado como pagado' : 'Payment marked as paid');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDeletePayment = async (payment) => {
    const confirmed = window.confirm(
      isSpanish
        ? '¿Borrar esta línea de nómina de dividendos?'
        : 'Delete this dividend payroll line?'
    );
    if (!confirmed) return;

    setDeletingPaymentId(payment._id);
    try {
      await deletePayment(payment._id);
      toast.success(isSpanish ? 'Línea eliminada' : 'Line deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setDeletingPaymentId(null);
    }
  };

  const totalPending = payments
    .filter((p) => p.status === 'pendiente' || p.status === 'aprobado')
    .reduce((sum, p) => sum + (p.total || 0), 0);

  const totalPaid = payments
    .filter((p) => p.status === 'pagado')
    .reduce((sum, p) => sum + (p.total || 0), 0);

  const totalDistributable = payments.reduce((sum, p) => sum + (p.total || 0), 0);

  return (
    <Layout title={t('payments')}>
      <div className="space-y-6" data-testid="payments-page">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="valhalla-card p-5 bg-gradient-to-br from-amber-500/20 to-amber-500/5 border-amber-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-amber-400">
                <Clock weight="duotone" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('pending')}</p>
                <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">{money(totalPending)}</p>
              </div>
            </div>
          </div>
          <div className="valhalla-card p-5 bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border-emerald-500/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-emerald-400">
                <Check weight="duotone" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">{t('paid')}</p>
                <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">{money(totalPaid)}</p>
              </div>
            </div>
          </div>
          <div className="valhalla-card p-5 bg-gradient-to-br from-[#E2B05E]/20 to-[#E2B05E]/5 border-[#E2B05E]/30">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-[#0B0E14]/50 text-[#E2B05E]">
                <Receipt weight="duotone" className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider">
                  {isSpanish ? 'Total distribuido' : 'Total distributed'}
                </p>
                <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">{money(totalDistributable)}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="valhalla-card p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="font-['Outfit'] text-xl font-semibold text-[#F3F4F6]">
                {isSpanish ? 'Calculo mensual de dividendos' : 'Monthly dividend calculation'}
              </h2>
              <p className="text-sm text-[#9CA3AF]">
                {isSpanish
                  ? 'Ingresa ingresos, egresos y cantidad de accionistas por cliente. El monto distribuible se reparte en partes iguales.'
                  : 'Enter income, expenses, and shareholder count per client. The distributable amount is split evenly.'}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Select value={selectedMonthNumber} onValueChange={(month) => setSelectedMonth(`${selectedYear}-${month}`)}>
                <SelectTrigger className="valhalla-input sm:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#151B26] border-[#242E42]">
                  {monthOptions.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {isSpanish ? month.es : month.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedYear} onValueChange={(year) => setSelectedMonth(`${year}-${selectedMonthNumber}`)}>
                <SelectTrigger className="valhalla-input sm:w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#151B26] border-[#242E42]">
                  {yearOptions.map((year) => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="sm:w-36 valhalla-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#151B26] border-[#242E42]">
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="pendiente">{t('pending')}</SelectItem>
                  <SelectItem value="aprobado">{t('approved')}</SelectItem>
                  <SelectItem value="pagado">{t('paid')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {clients.map((client) => {
            const form = formsByClient[client._id] || createClientForm();
            const preview = calculatePreview(form);
            const canSubmit = isHR || isManager;
            return (
              <div key={client._id} className="valhalla-card p-5">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-5">
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">{client.name}</h3>
                    <p className="text-sm text-[#9CA3AF]">{client.email || client.contact_name || 'Cliente'}</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs uppercase tracking-wider text-[#9CA3AF]">{isSpanish ? 'Por accionista' : 'Per shareholder'}</p>
                    <p className="font-['Outfit'] text-xl font-bold text-[#E2B05E]">{money(preview.perShareholder)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF] flex items-center gap-1">
                      <TrendUp className="w-4 h-4 text-emerald-400" />
                      {isSpanish ? 'Ingresos mensuales' : 'Monthly income'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.monthly_income}
                      onChange={(e) => setClientFormValue(client._id, 'monthly_income', e.target.value)}
                      className="valhalla-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF] flex items-center gap-1">
                      <TrendDown className="w-4 h-4 text-red-400" />
                      {isSpanish ? 'Egresos mensuales' : 'Monthly expenses'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.monthly_expenses}
                      onChange={(e) => setClientFormValue(client._id, 'monthly_expenses', e.target.value)}
                      className="valhalla-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF]">
                      {isSpanish ? 'Reserva variable (%)' : 'Variable reserve (%)'}
                    </Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={form.reserve_percentage}
                      onChange={(e) => setClientFormValue(client._id, 'reserve_percentage', e.target.value)}
                      className="valhalla-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#9CA3AF] flex items-center gap-1">
                      <UsersThree className="w-5 h-5 text-[#3AB8D6]" />
                      {isSpanish ? 'Accionistas' : 'Shareholders'}
                    </Label>
                    <Input
                      type="number"
                      step="1"
                      min="1"
                      value={form.shareholder_count}
                      onChange={(e) => setClientFormValue(client._id, 'shareholder_count', e.target.value)}
                      className="valhalla-input"
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div className="rounded-lg bg-[#0B0E14]/50 p-3">
                    <p className="text-[#9CA3AF]">{isSpanish ? 'Utilidad' : 'Profit'}</p>
                    <p className="font-semibold text-[#F3F4F6]">{money(preview.net)}</p>
                  </div>
                  <div className="rounded-lg bg-[#0B0E14]/50 p-3">
                    <p className="text-[#9CA3AF]">{isSpanish ? 'Reserva' : 'Reserve'}</p>
                    <p className="font-semibold text-[#F3F4F6]">{money(preview.reserve)}</p>
                  </div>
                  <div className="rounded-lg bg-[#0B0E14]/50 p-3 col-span-2">
                    <p className="text-[#9CA3AF]">{isSpanish ? 'Monto a distribuir' : 'Distributable amount'}</p>
                    <p className="font-['Outfit'] text-xl font-bold text-[#E2B05E]">{money(preview.distributable)}</p>
                  </div>
                </div>

                <div className="mt-5 flex justify-end">
                  <Button
                    onClick={() => handleCalculateDividends(client)}
                    disabled={!canSubmit || submittingClientId === client._id}
                    className="w-full sm:w-auto bg-[#E2B05E] hover:bg-[#F0C47A] text-[#0B0E14] font-semibold"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    {submittingClientId === client._id
                      ? (isSpanish ? 'Calculando...' : 'Calculating...')
                      : t('calculatePayroll')}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="space-y-3">
          <h2 className="font-['Outfit'] text-xl font-semibold text-[#F3F4F6]">
            {isSpanish ? 'Nomina de dividendos generada' : 'Generated dividend payroll'}
          </h2>
          {payments.map((payment) => (
            <div key={payment._id} className="valhalla-card p-5" data-testid={`payment-${payment._id}`}>
              <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h3 className="font-semibold text-[#F3F4F6]">{payment.client_name}</h3>
                    <StatusBadge value={payment.status} />
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#9CA3AF]">
                    <span>{payment.period_month}</span>
                    <span>{isSpanish ? 'Accionistas:' : 'Shareholders:'} {payment.shareholder_count}</span>
                    <span>{isSpanish ? 'Reserva:' : 'Reserve:'} {payment.reserve_percentage || 0}%</span>
                  </div>
                  {payment.distributions?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {payment.distributions.map((distribution, index) => (
                        <span key={distribution.stakeholder_id || distribution.shareholder_number || index} className="rounded-full bg-[#0B0E14] border border-[#242E42] px-3 py-1 text-xs text-[#F3F4F6]">
                          {distribution.stakeholder_name}: {money(distribution.amount)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm xl:min-w-[520px]">
                  <div>
                    <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Ingresos' : 'Income'}</p>
                    <p className="text-emerald-400 font-medium">{money(payment.monthly_income)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Egresos' : 'Expenses'}</p>
                    <p className="text-red-400 font-medium">{money(payment.monthly_expenses)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF]">{isSpanish ? 'Distribuible' : 'Distributable'}</p>
                    <p className="text-[#F3F4F6] font-medium">{money(payment.distributable_total)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF]">{t('total')}</p>
                    <p className="font-['Outfit'] text-xl font-bold text-[#E2B05E]">{money(payment.total)}</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  {payment.status === 'pendiente' && isManager && (
                    <Button size="sm" onClick={() => handleApprove(payment._id)} className="bg-blue-500 hover:bg-blue-600">
                      <Check className="w-4 h-4 mr-1" />
                      {t('approved')}
                    </Button>
                  )}
                  {payment.status === 'aprobado' && isHR && (
                    <Button size="sm" onClick={() => handleMarkPaid(payment._id)} className="bg-emerald-500 hover:bg-emerald-600">
                      <HandCoins className="w-4 h-4 mr-1" />
                      {t('markAsPaid')}
                    </Button>
                  )}
                  {payment.status !== 'pagado' && (isHR || isManager) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeletePayment(payment)}
                      disabled={deletingPaymentId === payment._id}
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    >
                      <Trash className="w-4 h-4 mr-1" />
                      {isSpanish ? 'Borrar' : 'Delete'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {payments.length === 0 && !loading && (
          <div className="valhalla-card p-12 text-center">
            <CurrencyDollar className="w-12 h-12 mx-auto text-[#9CA3AF] mb-4" />
            <p className="text-[#9CA3AF]">{t('noData')}</p>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default PaymentsPage;
